import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/layout/DashboardLayout';
import { useAuth, API } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { 
  User,
  Bell,
  Shield,
  CreditCard,
  LogOut,
  ChevronRight,
  Save,
  Flame,
  Zap,
  Activity,
  Calendar,
  Trophy,
  Dumbbell,
  Moon
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const SettingsPage = () => {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [profile, setProfile] = useState({
    name: user?.name || '',
    sport: user?.sport || '',
    role: user?.role || 'athlete'
  });

  const [notifications, setNotifications] = useState({
    recoveryAlerts: true,
    workoutReminders: true,
    weeklyDigest: true,
    parentNotifications: true
  });

  const [trainingMode, setTrainingMode] = useState('build');
  const [seasonPhase, setSeasonPhase] = useState('off_season');
  const [seasonLoading, setSeasonLoading] = useState(false);

  // Fetch training mode and season phase on mount
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const token = localStorage.getItem('aura_token');
        
        // Fetch training mode
        const modeResponse = await axios.get(`${API}/training-program/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (modeResponse.data.training_mode) {
          setTrainingMode(modeResponse.data.training_mode);
        }
        
        // Fetch athlete profile for season phase
        const profileResponse = await axios.get(`${API}/athlete/profile`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (profileResponse.data.season_phase) {
          setSeasonPhase(profileResponse.data.season_phase);
        }
      } catch (error) {
        // Use defaults
      }
    };
    if (user?.role === 'athlete') {
      fetchSettings();
    }
  }, [user]);

  const handleTrainingModeChange = async (newMode) => {
    try {
      const token = localStorage.getItem('aura_token');
      await axios.put(`${API}/training-program/mode`, 
        { training_mode: newMode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTrainingMode(newMode);
      toast.success(`Training mode set to ${newMode === 'build' ? 'Build' : 'Maintain'}`);
    } catch (error) {
      toast.error('Failed to update training mode');
    }
  };

  const handleSeasonPhaseChange = async (newPhase) => {
    setSeasonLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.post(`${API}/intelligence/update-season-phase`, 
        { season_phase: newPhase },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSeasonPhase(newPhase);
      toast.success(`Season updated to ${newPhase.replace('_', '-')}`, {
        description: response.data.impact?.workout_volume || ''
      });
    } catch (error) {
      toast.error('Failed to update season phase');
    } finally {
      setSeasonLoading(false);
    }
  };

  const seasonPhases = [
    { 
      value: 'off_season', 
      label: 'Off-Season', 
      icon: Dumbbell,
      color: 'aura-primary',
      description: 'Build phase - full volume training to improve strength, speed, and skills'
    },
    { 
      value: 'pre_season', 
      label: 'Pre-Season', 
      icon: Flame,
      color: 'orange-500',
      description: 'Taper phase - 70% volume, preparing for competition'
    },
    { 
      value: 'in_season', 
      label: 'In-Season', 
      icon: Trophy,
      color: 'green-500',
      description: 'Maintenance - lighter training to stay fresh for games'
    },
    { 
      value: 'post_season', 
      label: 'Post-Season', 
      icon: Moon,
      color: 'purple-500',
      description: 'Recovery phase - very light activity, let your body heal'
    }
  ];

  const sports = [
    'Football', 'Basketball', 'Soccer', 'Baseball', 'Swimming',
    'Track & Field', 'Tennis', 'Volleyball', 'Wrestling', 'Gymnastics',
    'Martial Arts', 'CrossFit', 'General Fitness', 'Other'
  ];

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('aura_token');
      const response = await axios.put(`${API}/auth/profile`, profile, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      updateUser(response.data);
      toast.success('Profile updated!');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <DashboardLayout title="Settings">
      <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
        {/* Profile Section */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <User className="w-5 h-5 text-aura-primary" />
              Profile
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 rounded-full bg-aura-primary flex items-center justify-center">
                {user?.picture ? (
                  <img src={user.picture} alt="" className="w-16 h-16 rounded-full object-cover" />
                ) : (
                  <span className="font-heading font-bold text-2xl text-white">
                    {user?.name?.charAt(0)?.toUpperCase() || 'A'}
                  </span>
                )}
              </div>
              <div>
                <p className="font-medium text-white">{user?.name}</p>
                <p className="text-sm text-aura-text-muted">{user?.email}</p>
                <p className="text-xs text-aura-primary uppercase tracking-wider mt-1">
                  {user?.role}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Full Name</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                className="h-12 bg-aura-bg border-white/10 text-white"
              />
            </div>

            {user?.role === 'athlete' && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-aura-text-subtle">Primary Sport</Label>
                <Select value={profile.sport} onValueChange={(v) => setProfile({ ...profile, sport: v })}>
                  <SelectTrigger className="h-12 bg-aura-bg border-white/10 text-white">
                    <SelectValue placeholder="Select your sport" />
                  </SelectTrigger>
                  <SelectContent className="bg-aura-surface border-white/10">
                    {sports.map((sport) => (
                      <SelectItem key={sport} value={sport.toLowerCase()} className="text-white">
                        {sport}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button 
              onClick={handleSaveProfile}
              disabled={loading}
              className="w-full bg-aura-primary hover:bg-aura-primary-hover text-white font-heading uppercase tracking-wider h-12"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-5 h-5 mr-2" />
                  Save Changes
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Season Phase - NEW */}
        {user?.role === 'athlete' && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Calendar className="w-5 h-5 text-green-500" />
                Season Phase
              </CardTitle>
              <p className="text-sm text-aura-text-muted mt-1">
                Your workouts, nutrition, and recovery adjust based on your current season
              </p>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                {seasonPhases.map((phase) => {
                  const PhaseIcon = phase.icon;
                  const isActive = seasonPhase === phase.value;
                  return (
                    <button
                      key={phase.value}
                      onClick={() => handleSeasonPhaseChange(phase.value)}
                      disabled={seasonLoading}
                      className={`p-4 rounded-xl border-2 transition-all text-left ${
                        isActive 
                          ? `border-${phase.color} bg-${phase.color}/10` 
                          : 'border-white/10 hover:border-white/30 bg-white/5'
                      } ${seasonLoading ? 'opacity-50' : ''}`}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <PhaseIcon className={`w-5 h-5 ${isActive ? `text-${phase.color}` : 'text-aura-text-muted'}`} />
                        <span className="font-heading font-bold text-white uppercase text-sm">{phase.label}</span>
                      </div>
                      <p className="text-xs text-aura-text-muted line-clamp-2">
                        {phase.description}
                      </p>
                      {isActive && (
                        <Badge className={`mt-2 bg-${phase.color}/20 text-${phase.color} border-0 text-xs`}>Active</Badge>
                      )}
                    </button>
                  );
                })}
              </div>
              <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                <p className="text-xs text-blue-300">
                  <strong>How it affects your plan:</strong> In-season reduces workout volume by 40% and avoids heavy training before games. Off-season enables full intensity for building.
                </p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Notification Preferences */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <Bell className="w-5 h-5 text-aura-warning" />
              Notifications
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between py-2">
              <div>
                <p className="font-medium text-white">Recovery Alerts</p>
                <p className="text-sm text-aura-text-muted">Get notified when recovery score drops</p>
              </div>
              <Switch
                checked={notifications.recoveryAlerts}
                onCheckedChange={(checked) => setNotifications({ ...notifications, recoveryAlerts: checked })}
              />
            </div>
            
            <div className="flex items-center justify-between py-2 border-t border-white/5">
              <div>
                <p className="font-medium text-white">Workout Reminders</p>
                <p className="text-sm text-aura-text-muted">Daily training reminders</p>
              </div>
              <Switch
                checked={notifications.workoutReminders}
                onCheckedChange={(checked) => setNotifications({ ...notifications, workoutReminders: checked })}
              />
            </div>

            <div className="flex items-center justify-between py-2 border-t border-white/5">
              <div>
                <p className="font-medium text-white">Weekly Digest</p>
                <p className="text-sm text-aura-text-muted">Summary of your week's progress</p>
              </div>
              <Switch
                checked={notifications.weeklyDigest}
                onCheckedChange={(checked) => setNotifications({ ...notifications, weeklyDigest: checked })}
              />
            </div>

            {user?.role === 'athlete' && (
              <div className="flex items-center justify-between py-2 border-t border-white/5">
                <div>
                  <p className="font-medium text-white">Parent Notifications</p>
                  <p className="text-sm text-aura-text-muted">Allow supporters to see your progress</p>
                </div>
                <Switch
                  checked={notifications.parentNotifications}
                  onCheckedChange={(checked) => setNotifications({ ...notifications, parentNotifications: checked })}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* Training Intensity Mode */}
        {user?.role === 'athlete' && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Activity className="w-5 h-5 text-aura-primary" />
                Training Intensity
              </CardTitle>
              <p className="text-sm text-aura-text-muted mt-1">
                Adjust your workout intensity based on your current season
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => handleTrainingModeChange('build')}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    trainingMode === 'build' 
                      ? 'border-aura-primary bg-aura-primary/10' 
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Flame className={`w-5 h-5 ${trainingMode === 'build' ? 'text-aura-primary' : 'text-aura-text-muted'}`} />
                    <span className="font-heading font-bold text-white uppercase text-sm">Build Mode</span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    Off-season focus. Higher intensity, more volume. Build strength & speed.
                  </p>
                  {trainingMode === 'build' && (
                    <Badge className="mt-2 bg-aura-primary/20 text-aura-primary border-0 text-xs">Active</Badge>
                  )}
                </button>

                <button
                  onClick={() => handleTrainingModeChange('maintain')}
                  className={`p-4 rounded-xl border-2 transition-all text-left ${
                    trainingMode === 'maintain' 
                      ? 'border-aura-secondary bg-aura-secondary/10' 
                      : 'border-white/10 hover:border-white/30 bg-white/5'
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Zap className={`w-5 h-5 ${trainingMode === 'maintain' ? 'text-aura-secondary' : 'text-aura-text-muted'}`} />
                    <span className="font-heading font-bold text-white uppercase text-sm">Maintain Mode</span>
                  </div>
                  <p className="text-xs text-aura-text-muted">
                    In-season focus. Lighter workouts, more recovery. Peak performance.
                  </p>
                  {trainingMode === 'maintain' && (
                    <Badge className="mt-2 bg-aura-secondary/20 text-aura-secondary border-0 text-xs">Active</Badge>
                  )}
                </button>
              </div>
              <p className="text-xs text-aura-text-subtle">
                This affects your 12-Week Program workouts and AI recommendations
              </p>
            </CardContent>
          </Card>
        )}

        {/* Alert Thresholds */}
        {user?.role === 'athlete' && (
          <Card className="bg-aura-surface border-white/10">
            <CardHeader className="pb-3">
              <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
                <Shield className="w-5 h-5 text-aura-secondary" />
                Supporter Alert Thresholds
              </CardTitle>
              <p className="text-sm text-aura-text-muted mt-1">
                Your supporters will be notified when these conditions are met
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="aura-card p-4">
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" className="rounded border-white/20" defaultChecked />
                  <span className="text-white text-sm">Recovery score drops below 50</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" className="rounded border-white/20" defaultChecked />
                  <span className="text-white text-sm">Miss 2+ workouts in a row</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <input type="checkbox" className="rounded border-white/20" />
                  <span className="text-white text-sm">Skip meal logging for a day</span>
                </div>
                <div className="flex items-center gap-2">
                  <input type="checkbox" className="rounded border-white/20" defaultChecked />
                  <span className="text-white text-sm">Complete a 7-day streak</span>
                </div>
              </div>
              <p className="text-xs text-aura-text-subtle">
                Your supporters will only see what you allow them to see
              </p>
            </CardContent>
          </Card>
        )}

        {/* Subscription (Placeholder) */}
        <Card className="bg-aura-surface border-white/10">
          <CardHeader className="pb-3">
            <CardTitle className="font-heading font-bold text-lg text-white uppercase tracking-wider flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-aura-success" />
              Subscription
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="aura-card p-4 bg-aura-success/10 border-aura-success/30">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-heading font-bold text-lg text-white">Free Trial</p>
                  <p className="text-sm text-aura-text-muted">14 days remaining</p>
                </div>
                <Button className="bg-aura-success hover:bg-aura-success/90 text-white font-heading uppercase tracking-wider">
                  Upgrade
                </Button>
              </div>
            </div>
            <div className="mt-4 space-y-2 text-sm text-aura-text-muted">
              <p>• $3/month for individual athletes</p>
              <p>• $1/month for each additional family member</p>
              <p>• $6/month max for families</p>
            </div>
          </CardContent>
        </Card>

        {/* Logout */}
        <Button 
          variant="outline"
          onClick={handleLogout}
          className="w-full border-aura-destructive/50 text-aura-destructive hover:bg-aura-destructive/10 font-heading uppercase tracking-wider h-12"
        >
          <LogOut className="w-5 h-5 mr-2" />
          Sign Out
        </Button>
      </div>
    </DashboardLayout>
  );
};

export default SettingsPage;
